'use client';

import { useEffect, useState } from 'react';
import PositionFilter, {
  PositionFilterType,
} from '../../../components/PositionFilter/PositionFilter';
import { useFriendlyViewPort } from '../../../hooks/useFriendlyViewPort';
import { useViewportSizeTracking } from '../../../hooks/useViewportSizeTracking';
import { useVal, useValRoute } from '../../../val/val.client';
import allEmployeesVal from '../../../data/employees.val';
import EmployeeData from '../../../components/Employee/Employee';
import LargeLayout from '../../../containers/LargeLayout';
import PageHeader from '../../../components/PageHeader/PageHeader';
import pageVal from './page.val';
import { notFound } from 'next/navigation';

const Employees = ({ params }: { params: Promise<never> }) => {
  const page = useValRoute(pageVal, params);
  const allEmployeesBySlug = useVal(allEmployeesVal);
  const employeesBySlug = Object.fromEntries(
    Object.entries(allEmployeesBySlug).filter(([, employee]) => !employee.hide)
  );
  useFriendlyViewPort();
  useViewportSizeTracking();

  // NOTE: the renderOrder is random and generated on render, to avoid infinite loops here we have to be a bit careful
  const employeeList = Object.values(employeesBySlug);
  const [selectedPosition, setSelectedPosition] =
    useState<PositionFilterType>('Alle');
  const [employeeRenderOrder, setEmployeeRenderOrder] = useState<number[]>();
  const lengthOfEmployees = Object.keys(employeesBySlug).length;

  useEffect(() => {
    setEmployeeRenderOrder(generateRenderOrder(lengthOfEmployees));
  }, [lengthOfEmployees]);

  const filteredEmployees = (employeeRenderOrder || []).flatMap((index) => {
    const employee = employeeList[index];
    if (!employee) {
      console.warn('Employee not found - random order failed');
      return [];
    }
    if (
      selectedPosition === 'Designere' &&
      !employee.position.includes('Design')
    ) {
      return [];
    }
    if (
      selectedPosition === 'Teknologer' &&
      !employee.position.includes('Teknolog')
    ) {
      return [];
    }

    return [employee];
  });

  if (!page) {
    return notFound();
  }
  const { header, subheader, pageImage } = page;
  return (
    <>
      <div
        id="menneskene"
        className="bg-blank-black text-blank-yellow no-transition-on-load transition-bg-1s"
      >
        <LargeLayout>
          <PageHeader header={header} subheader={subheader} image={pageImage} />

          <div className="w-full">
            <PositionFilter
              selectedPosition={selectedPosition}
              setSelectedPosition={setSelectedPosition}
            />
            <div
              className={
                'flex flex-col h-min w-full tablet:grid laptop:grid-cols-[repeat(4,minmax(0,1fr))] tablet:grid-cols-[repeat(2,minmax(0,1fr))] gap-y-64 gap-x-24'
              }
            >
              {filteredEmployees.map((employee) => {
                return (
                  <EmployeeData key={employee.email} employee={employee} />
                );
              })}
            </div>
          </div>
        </LargeLayout>
      </div>
    </>
  );
};

function shuffleList(list: number[]) {
  // Adapted from https://stackoverflow.com/a/12646864
  const listCopy = [...list];
  for (let i = listCopy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [listCopy[i], listCopy[j]] = [listCopy[j], listCopy[i]];
  }
  return listCopy;
}

function generateRenderOrder(length: number) {
  const numbers = Array.from(Array(length).keys());
  return shuffleList(numbers);
}

export default Employees;
